import {
	AbsoluteFill,
	interpolate,
	Sequence,
	useCurrentFrame,
	useVideoConfig,
	spring,
	Img,
} from 'remotion';
import { z } from 'zod';
import { zColor } from '@remotion/zod-types';

export const tauchforschungSchema = z.object({
	primaryColor: zColor(),
	secondaryColor: zColor(),
	accentColor: zColor(),
});

export const TauchforschungVideo: React.FC<z.infer<typeof tauchforschungSchema>> = ({
	primaryColor,
	secondaryColor,
	accentColor,
}) => {
	const frame = useCurrentFrame();
	const { durationInFrames } = useVideoConfig();

	// Fade in/out for entire composition
	const fadeIn = interpolate(frame, [0, 30], [0, 1], {
		extrapolateLeft: 'clamp',
		extrapolateRight: 'clamp',
	});

	const fadeOut = interpolate(
		frame,
		[durationInFrames - 30, durationInFrames],
		[1, 0],
		{
			extrapolateLeft: 'clamp',
			extrapolateRight: 'clamp',
		}
	);

	const opacity = Math.min(fadeIn, fadeOut);

	return (
		<AbsoluteFill style={{ backgroundColor: '#000', opacity }}>
			{/* Opening Title Sequence */}
			<Sequence durationInFrames={90}>
				<OpeningTitle
					primaryColor={primaryColor}
					accentColor={accentColor}
				/>
			</Sequence>

			{/* Image Carousel */}
			<Sequence from={60} durationInFrames={120}>
				<ImageCarousel
					primaryColor={primaryColor}
					secondaryColor={secondaryColor}
				/>
			</Sequence>

			{/* Cave Diving Text Overlay */}
			<Sequence from={90} durationInFrames={90}>
				<CaveDivingSection
					primaryColor={primaryColor}
					secondaryColor={secondaryColor}
				/>
			</Sequence>

			{/* Event Details */}
			<Sequence from={180} durationInFrames={90}>
				<EventDetails
					primaryColor={primaryColor}
					accentColor={accentColor}
				/>
			</Sequence>

			{/* Call to Action */}
			<Sequence from={240} durationInFrames={60}>
				<CallToAction
					primaryColor={primaryColor}
					accentColor={accentColor}
				/>
			</Sequence>
		</AbsoluteFill>
	);
};

const OpeningTitle: React.FC<{
	primaryColor: string;
	accentColor: string;
}> = ({ primaryColor, accentColor }) => {
	const frame = useCurrentFrame();
	const { fps } = useVideoConfig();

	const titleSpring = spring({
		frame,
		fps,
		config: { damping: 200 },
	});

	const titleY = interpolate(titleSpring, [0, 1], [50, 0]);
	const subtitleDelay = spring({
		frame: frame - 20,
		fps,
		config: { damping: 150 },
	});

	const subtitleY = interpolate(subtitleDelay, [0, 1], [30, 0]);

	return (
		<AbsoluteFill
			style={{
				justifyContent: 'center',
				alignItems: 'center',
				background: `linear-gradient(135deg, ${primaryColor}20, ${accentColor}10)`,
			}}
		>
			<div
				style={{
					transform: `translateY(${titleY}px)`,
					textAlign: 'center',
					color: primaryColor,
				}}
			>
				<h1
					style={{
						fontSize: 64,
						fontWeight: 'bold',
						margin: 0,
						textShadow: '2px 2px 4px rgba(0,0,0,0.8)',
						letterSpacing: '2px',
					}}
				>
					TAUCHFORSCHUNG
				</h1>
				<div
					style={{
						transform: `translateY(${subtitleY}px)`,
						marginTop: 20,
					}}
				>
					<h2
						style={{
							fontSize: 32,
							fontWeight: 300,
							margin: 0,
							color: accentColor,
							letterSpacing: '1px',
						}}
					>
						HIRLATZHÖHLE
					</h2>
				</div>
			</div>
		</AbsoluteFill>
	);
};

const ImageCarousel: React.FC<{
	primaryColor: string;
	secondaryColor: string;
}> = ({ primaryColor, secondaryColor }) => {
	const frame = useCurrentFrame();

	const images = [
'https://img.werkzeugh.at/q7PCaKUBdYluBm-NQc3-MBfxIXg=/2000x2000/uploads/original/ck/0310/qj9Hvys7nlvhThDtHc.jpg',
'https://img.werkzeugh.at/uV80i8rLV19dPQfyNisM2IjDZ90=/2000x2000/uploads/original/ck/0310/qihPPkiyq0aVonhZ5j.jpg',
'https://img.werkzeugh.at/434QfR8TK70oSYrXEG9gcywidAQ=/2000x2000/uploads/original/ck/0310/qihhXtTb52zwXTdXdJ.jpg',
'https://img.werkzeugh.at/FjBZOcRj4BeC4hZ-8rx7NltTY0A=/2000x2000/uploads/original/ck/0310/qj0fG7WBEzLXaEUnYW.jpg',
	];

	// Each image shows for 30 frames (1 second at 30fps)
	const imageIndex = Math.floor(frame / 30) % images.length;
	const imageFrame = frame % 30;

	// Slide transition effect
	const slideProgress = interpolate(imageFrame, [25, 30], [0, 1], {
		extrapolateLeft: 'clamp',
		extrapolateRight: 'clamp',
	});

	const currentImageX = interpolate(slideProgress, [0, 1], [0, -100]);
	const nextImageX = interpolate(slideProgress, [0, 1], [100, 0]);

	const nextImageIndex = (imageIndex + 1) % images.length;

	// Zoom effect for visual interest
	const zoom = interpolate(imageFrame, [0, 30], [1, 1.1], {
		extrapolateLeft: 'clamp',
		extrapolateRight: 'clamp',
	});

	return (
		<AbsoluteFill style={{ overflow: 'hidden' }}>
			{/* Current Image */}
			<AbsoluteFill
				style={{
					transform: `translateX(${currentImageX}%) scale(${zoom})`,
				}}
			>
				<Img
					src={images[imageIndex]}
					style={{
						width: '100%',
						height: '100%',
						objectFit: 'cover',
					}}
				/>
				<AbsoluteFill
					style={{
						background: `linear-gradient(to bottom, transparent, ${primaryColor}40, transparent)`,
					}}
				/>
			</AbsoluteFill>

			{/* Next Image (sliding in) */}
			{slideProgress > 0 && (
				<AbsoluteFill
					style={{
						transform: `translateX(${nextImageX}%)`,
					}}
				>
					<Img
						src={images[nextImageIndex]}
						style={{
							width: '100%',
							height: '100%',
							objectFit: 'cover',
						}}
					/>
					<AbsoluteFill
						style={{
							background: `linear-gradient(to bottom, transparent, ${secondaryColor}40, transparent)`,
						}}
					/>
				</AbsoluteFill>
			)}

			{/* Overlay gradient for text readability */}
			<AbsoluteFill
				style={{
					background: 'linear-gradient(to bottom, rgba(0,0,0,0.3), transparent, rgba(0,0,0,0.5))',
				}}
			/>
		</AbsoluteFill>
	);
};

const CaveDivingSection: React.FC<{
	primaryColor: string;
	secondaryColor: string;
}> = ({ primaryColor, secondaryColor }) => {
	const frame = useCurrentFrame();

	const slideIn = interpolate(frame, [0, 40], [-100, 0], {
		extrapolateLeft: 'clamp',
		extrapolateRight: 'clamp',
	});

	return (
		<AbsoluteFill
			style={{
				background: 'rgba(0,0,0,0.7)',
			}}
		>
			<div
				style={{
					transform: `translateX(${slideIn}%)`,
					height: '100%',
					display: 'flex',
					flexDirection: 'column',
					justifyContent: 'center',
					alignItems: 'center',
					color: '#ffffff',
				}}
			>
				<div
					style={{
						textAlign: 'center',
						padding: '0 60px',
					}}
				>
					<h3
						style={{
							fontSize: 48,
							fontWeight: 'bold',
							margin: '0 0 30px 0',
							textShadow: '2px 2px 4px rgba(0,0,0,0.9)',
							color: primaryColor,
						}}
					>
						EXTREME CAVE DIVING
					</h3>
					<p
						style={{
							fontSize: 24,
							lineHeight: 1.6,
							margin: 0,
							maxWidth: '600px',
							textShadow: '1px 1px 4px rgba(0,0,0,0.9)',
						}}
					>
						Exploring underwater siphons in one of Europe's most complex cave systems
						<br />
						<strong style={{ color: secondaryColor }}>
							100km+ cave system • 1000m deep
						</strong>
					</p>
				</div>
			</div>
		</AbsoluteFill>
	);
};

const EventDetails: React.FC<{
	primaryColor: string;
	accentColor: string;
}> = ({ primaryColor, accentColor }) => {
	const frame = useCurrentFrame();
	const { fps } = useVideoConfig();

	const detailsSpring = spring({
		frame,
		fps,
		config: { damping: 180 },
	});

	const scale = interpolate(detailsSpring, [0, 1], [0.8, 1]);

	return (
		<AbsoluteFill
			style={{
				justifyContent: 'center',
				alignItems: 'center',
				background: `linear-gradient(45deg, #000, ${primaryColor}10)`,
			}}
		>
			<div
				style={{
					transform: `scale(${scale})`,
					textAlign: 'center',
					color: primaryColor,
					padding: '40px',
					border: `2px solid ${accentColor}`,
					borderRadius: '20px',
					background: 'rgba(0,0,0,0.7)',
				}}
			>
				<h2
					style={{
						fontSize: 40,
						fontWeight: 'bold',
						margin: '0 0 20px 0',
						color: accentColor,
					}}
				>
					Barbara Wielander
				</h2>
				<div style={{ fontSize: 32, margin: '20px 0' }}>
					<strong>September 17th • 17:30</strong>
				</div>
				<div style={{ fontSize: 24, opacity: 0.9 }}>
					Tagferne Tauchforschung
					<br />
					Hirlatzhöhle near Hallstatt
				</div>
			</div>
		</AbsoluteFill>
	);
};

const CallToAction: React.FC<{
	primaryColor: string;
	accentColor: string;
}> = ({ primaryColor, accentColor }) => {
	const frame = useCurrentFrame();

	const pulse = Math.sin(frame * 0.2) * 0.1 + 1;

	return (
		<AbsoluteFill
			style={{
				justifyContent: 'center',
				alignItems: 'center',
				background: `radial-gradient(circle, ${accentColor}20, #000)`,
			}}
		>
			<div
				style={{
					textAlign: 'center',
					transform: `scale(${pulse})`,
				}}
			>
				<div
					style={{
						fontSize: 48,
						fontWeight: 'bold',
						color: accentColor,
						margin: '0 0 20px 0',
						textShadow: '2px 2px 4px rgba(0,0,0,0.8)',
					}}
				>
					DON'T MISS OUT
				</div>
				<div
					style={{
						fontSize: 24,
						color: primaryColor,
						margin: '10px 0',
					}}
				>
					werkzeugH.at
				</div>
				<div
					style={{
						fontSize: 20,
						color: primaryColor,
						opacity: 0.8,
					}}
				>
					#Tauchforschung #Hirlatzhöhle #CaveDiving
				</div>
			</div>
		</AbsoluteFill>
	);
};

;
    var _remotion_globalVariableA, _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
